import { createApp as createVueApp, createSSRApp } from 'vue'
import { createPinia } from 'pinia'
import { createAppVuetify } from '@/plugins/vuetify'
import App from '@/App.vue'
import { createAppRouter, setActiveRouter } from '@/router'
import { createAppI18n, setActiveI18n } from '@/i18n'
import type { Locale } from '@/locales'
import { vPermission } from '@/directives/permission'
import type { Router } from 'vue-router'
import type { Pinia } from 'pinia'
import type { App as VueApp } from 'vue'

export interface AppContext {
  app: VueApp
  router: Router
  pinia: Pinia
}

/**
 * `locale` matters on the server: each request renders one language and the
 * URL segment is the only thing that says which. Passing it in means the
 * instance is correct before the first render, with no post-hoc mutation.
 */
export function createApp(isSSR = false, locale?: Locale): AppContext {
  const app = isSSR ? createSSRApp(App) : createVueApp(App)
  const pinia = createPinia()
  const router = createAppRouter(isSSR)
  const vuetify = createAppVuetify(isSSR)
  const i18n = createAppI18n(locale)

  // Hand the mounted router and i18n instance to the modules that cannot call
  // useRouter()/useI18n() (stores, the axios interceptor). Never on the
  // server: one process serves many requests, each builds its own, and a
  // module-level slot would leak one request's instance into the next.
  if (!isSSR) {
    setActiveRouter(router)
    setActiveI18n(i18n)
  }

  app.use(pinia)
  app.use(router)
  app.use(vuetify)
  app.use(i18n)

  // Register custom directives
  app.directive('permission', vPermission)

  return { app, router, pinia }
}
